import React from 'react';
import { useColorScheme } from '@mui/material/styles';
import {
    DEFAULT_CHANNEL_COVER_DARK_MODE,
    DEFAULT_CHANNEL_COVER_LIGHT_MODE,
} from '../../config';
import { normalizeImageUrl } from '../../utils/image-url.utils';

const resolveColorScheme = (
    mode?: string | null,
    systemMode?: string | null
) => {
    const resolvedMode = mode === 'system' ? systemMode : mode;

    return resolvedMode === 'light' ? 'light' : 'dark';
};

export const getDefaultChannelCover = (colorScheme?: string | null) =>
    colorScheme === 'light'
        ? DEFAULT_CHANNEL_COVER_LIGHT_MODE
        : DEFAULT_CHANNEL_COVER_DARK_MODE;

export const useDefaultChannelCover = () => {
    const { mode, systemMode } = useColorScheme();

    return getDefaultChannelCover(resolveColorScheme(mode, systemMode));
};

interface ChannelCoverImageProps extends Omit<
    React.ImgHTMLAttributes<HTMLImageElement>,
    'src'
> {
    src?: string | null;
}

export const ChannelCoverImage = ({
    src,
    alt = '',
    onError,
    ...props
}: ChannelCoverImageProps) => {
    const defaultChannelCover = useDefaultChannelCover();
    const [failedSrc, setFailedSrc] = React.useState<string | null>(null);

    React.useEffect(() => {
        setFailedSrc(null);
    }, [src]);

    const normalizedSrc = normalizeImageUrl(src);
    const resolvedSrc =
        normalizedSrc && normalizedSrc !== failedSrc
            ? normalizedSrc
            : defaultChannelCover;

    const handleError = (event: React.SyntheticEvent<HTMLImageElement>) => {
        if (resolvedSrc !== defaultChannelCover) {
            setFailedSrc(resolvedSrc);
        }

        onError?.(event);
    };

    return <img {...props} alt={alt} src={resolvedSrc} onError={handleError} />;
};
